import React, { useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Chip,
  Box,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "../../JobBatch.module.css";
import PdfIcon from "../../../../../shared/assets/icons/PdfIcon.png";

const Incomplete = ({ forbatch, acceptedBatches, setAcceptedBatches }) => {
  const [expandedBatch, setExpandedBatch] = React.useState(null);

  const handleAccordionToggle = (batchTitle) => (event) => {
    event.stopPropagation();
    setExpandedBatch((prev) => (prev === batchTitle ? null : batchTitle));
  };

  // const handleMoreClick = (event, batchTitle) => {
  //   event.stopPropagation();
  // };

  // Handle the logic to update batches with jobs
  useEffect(() => {
    if (forbatch && acceptedBatches.length > 0) {
      const updatedBatches = acceptedBatches.map((batch) => ({
        ...batch,
        jobs: [],
      }));

      forbatch.forEach((job) => {
        const batch = updatedBatches.find(
          (batch) => batch._id === job.batchOfJobsForInstallationPartnerId
        );
        if (batch) {
          batch.jobs.push(job);
        }
      });

      // Filter jobs by status and completion date
      updatedBatches.forEach((batch) => {
        if (batch.jobs.length > 0) {
          const today = new Date();
          batch.jobs = batch.jobs.filter((job) => {
            const completionDate = new Date(job.jobCompletionDate);
            // Include completed jobs and pending jobs with a completion date in the past or today
            return (
              job.status === 2 || (job.status === 0 && completionDate <= today)
            );
          });

          // Calculate the date range for each batch (if any jobs left)
          if (batch.jobs.length > 0) {
            const jobDates = batch.jobs.map(
              (job) => new Date(job.jobStartingDate)
            );
            const completionDates = batch.jobs.map(
              (job) => new Date(job.jobCompletionDate)
            );

            const earliestDate = new Date(
              Math.min(...jobDates.map((date) => date.getTime()))
            );
            const latestDate = new Date(
              Math.max(...completionDates.map((date) => date.getTime()))
            );

            batch.dateRange = `${earliestDate.toLocaleDateString()} - ${latestDate.toLocaleDateString()}`;
          } else {
            batch.dateRange = "No Dates Available";
          }
        }
      });

      setAcceptedBatches((prevBatches) => {
        const prevBatchesString = JSON.stringify(prevBatches);
        const updatedBatchesString = JSON.stringify(updatedBatches);

        if (prevBatchesString !== updatedBatchesString) {
          return updatedBatches;
        } else {
          return prevBatches;
        }
      });
    }
  }, [forbatch, acceptedBatches, setAcceptedBatches]);

  return (
    <Grid className={`${styles.scrollableContainer}`}>
      {acceptedBatches?.filter((batch) =>
        batch.jobs?.some(
          (job) =>
            job.status === 0 && new Date(job.jobCompletionDate) < new Date()
        )
      ).length === 0 ? (
        <Typography
          className={`${styles.noJobsBox} ${styles.noJobsText}`}
          variant="h6"
          align="center"
        >
          No Batch of Jobs Available
        </Typography>
      ) : (
        acceptedBatches
          ?.filter((batch) =>
            batch.jobs?.some(
              (job) =>
                job.status === 0 && new Date(job.jobCompletionDate) < new Date()
            )
          ) // Filter batches with pending jobs whose jobCompletionDate is less than current date
          .map((batch, index) => (
            <Grid item xs={12} md={12} key={index} my={1} mx={2}>
              <Accordion expanded={expandedBatch === batch.batchTitle}>
                <AccordionSummary
                  expandIcon={
                    <IconButton
                      onClick={handleAccordionToggle(batch.batchTitle)}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  }
                  aria-controls={`panel-${index}-content`}
                  id={`panel-${index}-header`}
                >
                  <div className="flex items-center justify-between p-4 rounded-lg bg-white w-full mx-auto">
                    {/* Section 1: Batch Title */}
                    <div className="flex">
                      <h1 className={`${styles.batchTitle}`}>
                        {batch.batchTitle}
                      </h1>
                    </div>

                    {/* Section 2: Other Details */}
                    <div className="flex items-center gap-6">
                      {/* Date Range */}
                      <Chip label={batch.dateRange} className={styles.chip} />

                      {/* User Info */}
                      <Chip label={batch.contact} className={styles.chip} />

                      {/* Status Badge */}
                      <Chip label={batch.status} className={styles.pending} />

                      {/* Ellipsis Button */}
                      <button className="text-gray-400 hover:text-gray-600 text-xl">
                        ⋮
                      </button>
                    </div>
                  </div>
                </AccordionSummary>

                <AccordionDetails className={styles.accordionDetails}>
                  {/* Render Job Details Table */}
                  <Box className={`${styles.scrollableBox}`}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow className={styles.tableHeader}>
                            <TableCell className={styles.tableCell}>
                              ADDRESS
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              STORE MANAGER
                            </TableCell>

                            <TableCell className={styles.tableCell}>
                              JOB DATE
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              JOB PAY
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              KAM APPROVAL
                            </TableCell>

                            <TableCell className={styles.tableCell}>
                              SITE REPORT
                            </TableCell>

                            <TableCell className={styles.tableCell}>
                              JOB STATUS
                            </TableCell>
                            <TableCell className={styles.tableCell}>
                              IP PAY
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {batch.jobs.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell className={styles.tableCell}>
                                <div className={styles.sharedTextStyle}>
                                  {row.address}
                                </div>
                                <div className={styles.address2}>
                                  {row.address2}
                                </div>
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                <div className={styles.sharedTextStyle}>
                                  {row.storeManagerName}
                                </div>
                                <div className={styles.storeManagerContact}>
                                  {row.storeOwnerPhoneNumber}
                                </div>
                              </TableCell>

                              <TableCell className={styles.tableCell}>
                                <div className={styles.sharedTextStyle}>
                                  {row.jobDate}
                                </div>
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                <div className={styles.sharedTextStyle}>
                                  ₹{row.jobPay}
                                </div>
                              </TableCell>

                              <TableCell className={styles.tableCell}>
                                <div className={styles.sharedTextStyle}>
                                  {row.kamApproval}
                                </div>
                              </TableCell>

                              <TableCell className={styles.tableCell}>
                                <div className={styles.linkWithImage}>
                                  <img
                                    src={PdfIcon}
                                    alt="PDF Icon"
                                    className={styles.icon}
                                  />
                                  <a href="#" className={styles.link}>
                                    {row.SiteReport}
                                  </a>
                                </div>
                              </TableCell>

                              <TableCell className={styles.tableCell}>
                                {/* <Chip label={row.jobStatus} className={styles.pending} /> */}
                                <Chip
                                  label={row.jobStatus}
                                  className={
                                    row.jobStatus === "Complete"
                                      ? styles.amountPaid
                                      : styles.pending
                                  }
                                />
                              </TableCell>
                              <TableCell className={styles.tableCell}>
                                {/* <Chip label={row.ipPay} className={styles.pending}

                            /> */}
                                <Chip
                                  label={row.ipPay}
                                  className={
                                    row.ipPay === "Paid"
                                      ? styles.amountPaid
                                      : styles.pending
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))
      )}
    </Grid>
  );
};

export default Incomplete;
